"use client";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@universe/ui/components/alert-dialog";
import { buttonVariants } from "@universe/ui/components/button";
import { Loader2 } from "lucide-react";
import type * as React from "react";

// The alert-dialog's action merges this over its default fill (tailwind-merge
// drops the conflicting primary classes), so a destructive confirm paints
// exactly as a `variant="destructive"` Button does.
const DESTRUCTIVE_ACTION_CLASSES = buttonVariants({ variant: "destructive" });

interface ConfirmDialogProps {
  /**
   * Controlled-mode props. Optional when `trigger` is given: Radix then keeps
   * its own open state.
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Wrapped in the dialog's trigger (as its child) when given. */
  trigger?: React.ReactNode;
  title: React.ReactNode;
  description: React.ReactNode;
  cancelLabel: React.ReactNode;
  confirmLabel: React.ReactNode;
  onConfirm: () => void | Promise<void>;
  /** Paints the confirm action with the shared destructive variant. */
  destructive?: boolean;
  /**
   * Keeps the dialog open after the confirm click, for a caller whose action
   * takes a moment: it shows `isPending` meanwhile and closes the dialog (or
   * navigates away) itself.
   */
  stayOpenOnConfirm?: boolean;
  /**
   * Disables the confirm action and shows a spinner beside `pendingLabel`
   * (or `confirmLabel`). Cancel stays enabled so the user can still leave.
   */
  isPending?: boolean;
  pendingLabel?: React.ReactNode;
  cancelTestId?: string;
  confirmTestId?: string;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  trigger,
  title,
  description,
  cancelLabel,
  confirmLabel,
  onConfirm,
  destructive = false,
  stayOpenOnConfirm = false,
  isPending = false,
  pendingLabel,
  cancelTestId,
  confirmTestId,
}: ConfirmDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      {trigger && <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>}
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={cancelTestId}>
            {cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid={confirmTestId}
            disabled={isPending}
            onClick={(event) => {
              if (stayOpenOnConfirm) {
                event.preventDefault();
              }
              void onConfirm();
            }}
            className={destructive ? DESTRUCTIVE_ACTION_CLASSES : undefined}
          >
            {isPending ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>{pendingLabel ?? confirmLabel}</span>
              </span>
            ) : (
              <span>{confirmLabel}</span>
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
